import { FormField, FormLabel } from "@/components/ui/form";
import { StepperButtons } from "../buttons";
import { StepperSidebar } from "../sidebar";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { Field, FieldContent, FieldGroup, FieldLabel, FieldLegend, FieldSet, FieldTitle } from "@/components/ui/field";
import { cn, zodEnum } from "@/lib/utils";
import { commissionStructures } from "@/lib/app";
import { RateCombobox } from "@/plans/edit/accelerators/components/rate-combobox";
import { Button } from "@/components/ui/button";
import { Plus, XIcon } from "lucide-react";
import { QuotaCombobox } from "@/plans/edit/accelerators/components/quota-combobox";
import { Input } from "@/components/ui/input";
import { useStepper } from "@/components/ui/stepper";
import { FixedAmountCombobox } from "@/plans/edit/accelerators/components/fixed-amount-combobox";


const ruleBuilderSchema = z.object({
  commission_structure: z.enum(
    zodEnum(commissionStructures),
    {
      required_error: "Please select the basis for commission payments.",
    }
  ),
  quota: z.object({
    id: z.number().optional(),
    key: z.object({
      key_name: z.string({
        required_error: "Please select a key.",
      }),
      quota_id: z.number().optional(),
    }),
    operator: z.string({
      required_error: "Please select an operator.",
    }),
    value: z.string({
      required_error: "Please set a value.",
    }),
    value_from: z.string({
      required_error: "Please set a value.",
    }),
    value_to: z.string({
      required_error: "Please set a value.",
    }),
    value_list: z.string({
      required_error: "Please set a value.",
    }),
    value_arr: z.array(z.string()),
  }).optional(),
  rates: z.array(
    z.object({
      operator: z.string(),
      attainment: z.string(),
      attainment_range_from: z.string(),
      attainment_range_to: z.string(),
      rate: z.string(),
    }).optional(),
  ).optional(),
  fixed_amounts: z.array(
    z.object({
      operator: z.string(),
      attainment: z.string(),
      attainment_range_from: z.string(),
      attainment_range_to: z.string(),
      amount: z.string(),
      currency_id: z.string(),
    }).optional(),
  ).optional(),
  fixed_rate: z.object({
    amount: z.string(),
    currency_id: z.string(),
  }).optional(),
});

type RuleBuilderForm = z.infer<typeof ruleBuilderSchema>;

export function Rates() {
  const { onPrev } = useStepper();

  const rate = {
    operator: "gte",
    attainment: "100",
    attainment_range_from: "100",
    attainment_range_to: "120",
    rate: "5",
  };

  const fixed_amount = {
    operator: "gte",
    attainment: "100",
    attainment_range_from: "100",
    attainment_range_to: "120",
    amount: "100000",
    currency_id: "",
  };

  const defaultValues: Partial<RuleBuilderForm> = {
    commission_structure: "accelerated",
    quota: undefined,
    rates: [rate],
    fixed_amounts: [fixed_amount],
  };

  const form = useForm<RuleBuilderForm>({
    resolver: zodResolver(ruleBuilderSchema),
    defaultValues,
    mode: "onChange",
  });

  const { fields: rateFields, append: appendRate, remove: removeRate } = useFieldArray({
    name: "rates",
    control: form.control,
  });

  const { fields: fixedAmountFields, append: appendFixedAmount, remove: removeFixedAmount } = useFieldArray({
    name: "fixed_amounts",
    control: form.control,
  });

  function onRemoveQuota() {
    form.setValue("quota", undefined);
  }

  return (
    <div className="flex flex-row gap-x-10 justify-stretch">
      {/* Form */}
      <div className="flex flex-col justify-between gap-y-10 flex-1 min-h-[300px]">
        <Form {...form}>
          <form onSubmit={() => { }} className="flex flex-col gap-y-12">
            <FormField
              control={form.control}
              name="commission_structure"
              render={({ field }) => (
                <FieldGroup className="w-full max-w-xs">
                  <FieldSet>
                    <FieldLegend variant="label">
                      Commission structure
                    </FieldLegend>
                    <RadioGroup
                      defaultValue={field.value}
                      className="flex flex-row gap-x-2"
                      onValueChange={field.onChange}
                    >
                      <FieldLabel htmlFor="accelerated">
                        <Field orientation="horizontal">
                          <FieldContent className="w-24">
                            <FieldTitle>Accelerated</FieldTitle>
                          </FieldContent>
                          <RadioGroupItem value="accelerated" id="accelerated" className="invisible" />
                        </Field>
                      </FieldLabel>
                      <FieldLabel htmlFor="tiered">
                        <Field orientation="horizontal">
                          <FieldContent className="w-24">
                            <FieldTitle>Tiered</FieldTitle>
                          </FieldContent>
                          <RadioGroupItem value="tiered" id="tiered" className="invisible" />
                        </Field>
                      </FieldLabel>
                      <FieldLabel htmlFor="fixed_amount">
                        <Field orientation="horizontal">
                          <FieldContent className="w-24">
                            <FieldTitle>Fixed amount</FieldTitle>
                          </FieldContent>
                          <RadioGroupItem value="fixed_amount" id="fixed_amount" className="invisible" />
                        </Field>
                      </FieldLabel>
                    </RadioGroup>
                  </FieldSet>
                </FieldGroup>
              )}
            />

            <div className="flex flex-row items-end">
              <div>
                <FormLabel>Quota</FormLabel>
                <FormField
                  control={form.control}
                  name="quota.key"
                  render={({ field }) => (
                    <QuotaCombobox
                      value={field.value}
                      onChange={field.onChange}
                      onRemoveQuota={onRemoveQuota}
                      className="w-[500px]"
                    />
                  )}
                />
              </div>
              {form.watch("quota")?.key?.key_name === "custom_target" && (
                <div>
                  <FormLabel>Amount</FormLabel>
                  <Input className="w-[160px] h-9" />
                </div>
              )}
            </div>

            {form.watch("commission_structure") !== "fixed_amount" && (
              <div className="flex flex-col gap-y-1">
                {rateFields.map((field, index) => (
                  <div key={index} className="flex flex-row gap-2 items-start">
                    <FormField
                      control={form.control}
                      key={`${field.id}.rates.${index}`}
                      name={`rates.${index}`}
                      render={({ field: { value, onChange } }) => (
                        <RateCombobox
                          value={value}
                          onChange={onChange}
                          showLabels={index === 0}
                          onRemove={form.watch("rates")?.length === 1 ? undefined : () => removeRate(index)}
                        />
                      )}
                    />
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2 w-fit"
                  onClick={() => appendRate(rate)}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Add Rate
                </Button>
              </div>
            )}

            {form.watch("commission_structure") === "fixed_amount" && (
              <div className="flex flex-col gap-y-1">
                {fixedAmountFields.map((field, index) => (
                  <div key={index} className="flex flex-row gap-2 items-start">
                    <FormField
                      control={form.control}
                      key={`${field.id}.fixed_amounts.${index}`}
                      name={`fixed_amounts.${index}`}
                      render={({ field: { value, onChange } }) => (
                        <FixedAmountCombobox
                          value={value}
                          onChange={onChange}
                          showLabels={index === 0}
                          onRemove={form.watch("fixed_amounts")?.length === 1 ? undefined : () => removeFixedAmount(index)}
                        />
                      )}
                    />
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2 w-fit"
                  onClick={() => appendFixedAmount(fixed_amount)}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Add Rate
                </Button>
              </div>
            )}

            {/* {form.watch("commission_structure") === "fixed_amount" && ( */}
            {/*   <FixedAmountForm */}
            {/*     value={{ amount: "", currency_id: "" }} */}
            {/*     onChange={() => { }} */}
            {/*   /> */}
            {/* )} */}

          </form>
        </Form>
        <StepperButtons
          onPrev={onPrev}
        />
      </div>
      <StepperSidebar />
    </div>
  );
}
